import { getSubscriptionStartDiscountFromLocationId } from './utils';
import { AmountObject, ById, ShopPublicInfo } from 'common/types';
import { PurchasedPlan } from 'common/modules/plans';
import { BillingCreditsBalance, BILLING_CURRENCY } from 'common/modules/billing';
import { ShopReadOnlyData } from 'common/types';
import {
	getLocationSubscriptionPrice,
	getSubscriptionCancelRefund,
} from 'common/modules/plans/subscriptions';
import { getShopLocations } from 'common/utils/shopUtils';
import {
	addCredits,
	getCreditsBalanceObject,
	getCreditsToUseAndBalanceAfter,
} from 'common/modules/billing/utils/credits';
import { multiplyPrice, sumPrices } from 'common/utils/pricing';
import moment from 'moment';
import { constrain } from 'common/utils/math';

export interface SubscriptionPriceBreakdown {
	totalSubscriptionPrice: AmountObject;
	totalRefundCredits: AmountObject;
	totalStartDiscount: AmountObject;
	totalCancelRefund: AmountObject;
	totalRevenueDiscount: AmountObject;
	totalLeftToPay: AmountObject;
	totalCreditsToUse: {
		refund: number;
		manual: number;
		total: number;
	};

	creditsBalance: BillingCreditsBalance;
}

export const getPurchaseSubscriptionPriceBreakdown = (args: {
	shopPublicInfo: ShopPublicInfo;
	shopReadOnly: ShopReadOnlyData;
	newPlan: PurchasedPlan;
	discountEligibilityByLocation: ById<boolean>;
	date: moment.Moment;
}): SubscriptionPriceBreakdown => {
	const { shopPublicInfo, shopReadOnly, newPlan, discountEligibilityByLocation, date } = args;
	const locationIds = getShopLocations(shopPublicInfo).map((l) => l.id);
	const creditsBalanceBefore = getCreditsBalanceObject(shopReadOnly);

	return locationIds.reduce(
		(result, locationId) => {
			const locationBreakdown = getSubscriptionPriceBreakdownForLocation({
				locationId,
				creditsBalance: result.creditsBalance,
				hasRevenueDiscount: discountEligibilityByLocation[locationId],
				shopReadOnly,
				newPlan,
				date,
			});

			result.creditsBalance = locationBreakdown.creditsBalance;
			result.totalSubscriptionPrice = sumPrices([
				result.totalSubscriptionPrice,
				locationBreakdown.subscriptionPrice,
			]);
			result.totalRefundCredits = sumPrices([
				result.totalRefundCredits,
				locationBreakdown.refundCredits,
			]);
			result.totalStartDiscount = sumPrices([
				result.totalStartDiscount,
				locationBreakdown.startDiscount,
			]);
			result.totalCancelRefund = sumPrices([
				result.totalCancelRefund,
				locationBreakdown.cancelRefund,
			]);
			result.totalRevenueDiscount = sumPrices([
				result.totalRevenueDiscount,
				locationBreakdown.revenueDiscount,
			]);
			result.totalLeftToPay = sumPrices([result.totalLeftToPay, locationBreakdown.leftToPay]);
			result.totalCreditsToUse.refund += locationBreakdown.creditsToUse.refund;
			result.totalCreditsToUse.manual += locationBreakdown.creditsToUse.manual;
			result.totalCreditsToUse.total += locationBreakdown.creditsToUse.total;

			return result;
		},
		{
			totalSubscriptionPrice: {
				value: 0,
				currency: BILLING_CURRENCY.code,
			},
			totalRefundCredits: {
				value: 0,
				currency: BILLING_CURRENCY.code,
			},
			totalStartDiscount: {
				value: 0,
				currency: BILLING_CURRENCY.code,
			},
			totalCancelRefund: {
				value: 0,
				currency: BILLING_CURRENCY.code,
			},
			totalRevenueDiscount: {
				value: 0,
				currency: BILLING_CURRENCY.code,
			},
			totalLeftToPay: {
				value: 0,
				currency: BILLING_CURRENCY.code,
			},
			totalCreditsToUse: {
				refund: 0,
				manual: 0,
				total: 0,
			},
			creditsBalance: creditsBalanceBefore,
		},
	);
};

export interface SubscriptionPriceBreakdownForLocation {
	subscriptionPrice: AmountObject;
	refundCredits: AmountObject;
	startDiscount: AmountObject;
	cancelRefund: AmountObject;
	revenueDiscount: AmountObject;
	leftToPay: AmountObject;
	refundableAmount: AmountObject;
	hasRevenueDiscount: boolean;
	creditsToUse: {
		refund: number;
		manual: number;
		total: number;
	};
	creditsBalance: BillingCreditsBalance;
}

export const getSubscriptionPriceBreakdownForLocation = (args: {
	locationId: string;
	creditsBalance: BillingCreditsBalance;
	shopReadOnly: ShopReadOnlyData;
	newPlan: PurchasedPlan;
	hasRevenueDiscount: boolean;
	date: moment.Moment;
}): SubscriptionPriceBreakdownForLocation => {
	const { locationId, shopReadOnly, newPlan, hasRevenueDiscount, date } = args;

	const cancelRefund = getSubscriptionCancelRefund({
		plan: shopReadOnly.features.plan,
		locationId,
		cancellationDate: date,
	});

	const startDiscount = multiplyPrice(
		getSubscriptionStartDiscountFromLocationId({
			plan: newPlan,
			locationId,
			startDate: date,
		}),
		hasRevenueDiscount ? 0.5 : 1,
	);

	const subscriptionPrice = getLocationSubscriptionPrice(newPlan, locationId);

	const revenueDiscount =
		!!subscriptionPrice && hasRevenueDiscount
			? multiplyPrice(subscriptionPrice, 0.5)
			: { currency: BILLING_CURRENCY.code, value: 0 };

	const priceAfterRevenueAndStartDiscount = sumPrices([
		subscriptionPrice,
		multiplyPrice(revenueDiscount, -1),
		multiplyPrice(startDiscount, -1),
	]);

	const cancelRefundToUseAsDiscount = constrain(
		0,
		priceAfterRevenueAndStartDiscount.value,
	)(cancelRefund.value);
	const refundCreditsGained = cancelRefund.value - cancelRefundToUseAsDiscount;
	const creditsBalanceAfterRefund = addCredits({
		creditsBalance: args.creditsBalance,
		amount: refundCreditsGained,
		type: 'refund',
	});

	const priceAfterAllDiscounts = sumPrices([
		priceAfterRevenueAndStartDiscount,
		multiplyPrice(
			{
				value: cancelRefundToUseAsDiscount,
				currency: BILLING_CURRENCY.code,
			},
			-1,
		),
	]);

	const { creditsToUse, balanceAfter } = getCreditsToUseAndBalanceAfter({
		creditsBalance: creditsBalanceAfterRefund,
		creditsToUse: priceAfterAllDiscounts.value,
		type: 'any',
	});

	const leftToPay = sumPrices([
		priceAfterAllDiscounts,
		multiplyPrice(
			{
				value: creditsToUse.total,
				currency: BILLING_CURRENCY.code,
			},
			-1,
		),
	]);

	const refundableAmount = sumPrices([
		leftToPay,
		{
			value: cancelRefundToUseAsDiscount,
			currency: BILLING_CURRENCY.code,
		},
		{
			value: creditsToUse.refund,
			currency: BILLING_CURRENCY.code,
		},
	]);

	return {
		creditsBalance: balanceAfter,
		subscriptionPrice,
		startDiscount,
		refundCredits: {
			value: refundCreditsGained,
			currency: BILLING_CURRENCY.code,
		},
		cancelRefund: {
			value: cancelRefundToUseAsDiscount,
			currency: BILLING_CURRENCY.code,
		},
		refundableAmount,
		revenueDiscount,
		leftToPay,
		creditsToUse,
		hasRevenueDiscount,
	};
};
